import { t as _objectSpread2 } from "./objectSpread2-CtW7yo8J.js";
import { C as noop, D as SOURCE_IDS, E as identity, O as getCollection, S as scrollTo, T as isModifierEvent, _ as Hit, a as NoResultsScreen, b as SearchBoxForm, c as Results, d as useRefreshOnInitialQuery, f as useModalRefs, g as useDocSearchFacets, h as useSendItemClickEvent, i as ResultsScreen, l as useStoredDocSearches, m as useInitialModalQuery, n as buildNoQuerySources, o as ErrorScreen, p as useModalEnvironment, r as buildQuerySources, s as StoredSearchesSections, t as normalizeDocSearchIndexes, u as useSaveRecentSearch, v as Footer, w as isQueryEmpty, x as FacetBar, y as ModalShell } from "./normalizeDocSearchIndexes-BW-QYGAy.js";
import { l as CloseIcon, p as LoadingIcon, t as useSearchClient } from "./useSearchClient-Dfh7Oq9Z.js";
import { t as SearchIcon } from "./SearchIcon-DbxRKLtx.js";
import { t as _objectWithoutProperties } from "./objectWithoutProperties-mN58NJZT.js";
import { t as SparklesIcon } from "./SparklesIcon-D0XWamV3.js";
import { C as ConversationHistoryIcon, E as AlertIcon, S as NewConversationIcon, T as StopIcon, _ as showAskAiBlockingBannerNewConversationLink, a as MemoizedMarkdown, b as ConversationPromptSuggestions, c as buildDummyAskAiHit, d as getAskAiBlockingBannerMessage, f as getMessageContent, g as isThreadDepthError, h as isAskAiPromptBlockingError, i as groupConsecutiveToolResults, l as extractLinksFromMessage, m as isAgentStudioTokenOutputLimitError, n as useAskAi, o as ToolCall, p as isAIToolPart, r as Menu, s as EMPTY_TOOLS, t as useSuggestedQuestions, u as getAgentPromptSuggestions, v as SourcesPanel, w as MoreVerticalIcon, x as BackIcon, y as FeedbackActions } from "./useSuggestedQuestions-5hGD5hMi.js";
import React, { Fragment, useMemo } from "react";
import { createAutocomplete } from "@algolia/autocomplete-core";

//#region src/icons/MessageIcon.tsx
function MessageIcon() {
	return /* @__PURE__ */ React.createElement("svg", {
		xmlns: "http://www.w3.org/2000/svg",
		width: "13",
		height: "13",
		viewBox: "0 0 24 24",
		fill: "none",
		stroke: "currentColor",
		strokeWidth: "2",
		strokeLinecap: "round",
		strokeLinejoin: "round"
	}, /* @__PURE__ */ React.createElement("path", { d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" }));
}

//#endregion
//#region src/AggregatedSearchBlock.tsx
const defaultAggregatedToolCallParts = {
	before: "Searched for ",
	separator: ", ",
	lastSeparator: " and ",
	after: ""
};
function AggregatedSearchBlock({ queries, translations, onSearchQueryClick }) {
	if (queries.length === 0) return null;
	if (typeof translations.aggregatedToolCallNode === "function") return /* @__PURE__ */ React.createElement(React.Fragment, null, translations.aggregatedToolCallNode(queries, onSearchQueryClick));
	const { before = "", separator = ", ", lastSeparator = " and ", after = "" } = (translations.aggregatedToolCallText ? translations.aggregatedToolCallText(queries) : defaultAggregatedToolCallParts) || {};
	return /* @__PURE__ */ React.createElement("div", { className: "DocSearch-AskAiScreen-MessageContent-Tool Tool--AggregatedResult" }, /* @__PURE__ */ React.createElement(SearchIcon, { size: 18 }), /* @__PURE__ */ React.createElement("span", null, before && /* @__PURE__ */ React.createElement("span", null, before), queries.map((q, idx) => /* @__PURE__ */ React.createElement(Fragment, { key: q + idx }, /* @__PURE__ */ React.createElement("span", {
		role: "button",
		tabIndex: 0,
		className: "DocSearch-AskAiScreen-MessageContent-Tool-Query",
		onKeyDown: (e) => {
			if (e.key === "Enter" || e.key === " ") {
				e.preventDefault();
				onSearchQueryClick(q);
			}
		},
		onClick: () => onSearchQueryClick(q)
	}, "\"", q, "\""), idx < queries.length - 2 && separator, idx === queries.length - 2 && lastSeparator)), after && /* @__PURE__ */ React.createElement("span", null, after)));
}

//#endregion
//#region src/AskAiScreen.tsx
const _excluded$6 = ["translations"];
/**
* Maps AskAiScreen's public translation keys to the shared
* `ToolCallTranslations` shape consumed by the `ToolCall` component, applying
* default English values.
*/
function toToolCallTranslations(translations) {
	const { preToolCallText = "Searching...", duringToolCallText = "Searching...", afterToolCallText = "Searched for", savedMemoryToolResultText = "Saved to memory", memoryToolResultText = "Used memory to enhance results" } = translations;
	return {
		preToolCallText,
		searchingText: duringToolCallText,
		toolCallResultText: afterToolCallText,
		savedMemoryToolResultText,
		memoryToolResultText
	};
}
function AskAiScreenDisclaimer({ disclaimerText }) {
	return /* @__PURE__ */ React.createElement("p", { className: "DocSearch-AskAiScreen-Disclaimer" }, /* @__PURE__ */ React.createElement(SparklesIcon, null), " ", disclaimerText);
}
function AskAiExchangeCard({ exchange, askAiError, isLastExchange, loadingStatus, onSearchQueryClick, translations, tools, conversations, onFeedback, memoryEnabled, onSelectPromptSuggestion }) {
	var _userMessage$metadata, _assistantMessage$met;
	const { userMessage, assistantMessage } = exchange;
	const { stoppedStreamingText = "You stopped this response", errorTitleText = "Chat error", relatedSourcesText, relatedSourcesTextPlural, suggestedPromptsTitleText = "Suggested prompts" } = translations;
	const toolCallTranslations = useMemo(() => toToolCallTranslations(translations), [translations]);
	const isPromptBlockingError = isAskAiPromptBlockingError(askAiError);
	const assistantContent = useMemo(() => getMessageContent(assistantMessage), [assistantMessage]);
	const userContent = useMemo(() => getMessageContent(userMessage), [userMessage]);
	const urlsToDisplay = React.useMemo(() => extractLinksFromMessage(assistantMessage), [assistantMessage]);
	const displayParts = React.useMemo(() => {
		return groupConsecutiveToolResults((assistantMessage === null || assistantMessage === void 0 ? void 0 : assistantMessage.parts) || []);
	}, [assistantMessage]);
	const promptSuggestions = useMemo(() => {
		if (!isLastExchange) return [];
		return getAgentPromptSuggestions((assistantMessage === null || assistantMessage === void 0 ? void 0 : assistantMessage.parts) || []);
	}, [assistantMessage, isLastExchange]);
	const wasStopped = ((_userMessage$metadata = userMessage.metadata) === null || _userMessage$metadata === void 0 ? void 0 : _userMessage$metadata.stopped) || (assistantMessage === null || assistantMessage === void 0 || (_assistantMessage$met = assistantMessage.metadata) === null || _assistantMessage$met === void 0 ? void 0 : _assistantMessage$met.stopped);
	const showActions = !wasStopped && (!isLastExchange || isLastExchange && loadingStatus === "ready" && Boolean(assistantMessage));
	const isThinking = ["submitted", "streaming"].includes(loadingStatus) && isLastExchange && !displayParts.some((part) => part.type !== "step-start");
	const messageId = (assistantMessage === null || assistantMessage === void 0 ? void 0 : assistantMessage.id) || exchange.id;
	return /* @__PURE__ */ React.createElement("div", { className: "DocSearch-AskAiScreen-Response-Container" }, /* @__PURE__ */ React.createElement("div", { className: "DocSearch-AskAiScreen-Response" }, /* @__PURE__ */ React.createElement("div", { className: "DocSearch-AskAiScreen-Message DocSearch-AskAiScreen-Message--user" }, /* @__PURE__ */ React.createElement("p", { className: "DocSearch-AskAiScreen-Query" }, userContent !== null && userContent !== void 0 ? userContent : "")), /* @__PURE__ */ React.createElement("div", { className: "DocSearch-AskAiScreen-Message DocSearch-AskAiScreen-Message--assistant" }, /* @__PURE__ */ React.createElement("div", { className: "DocSearch-AskAiScreen-MessageContent" }, loadingStatus === "error" && askAiError && isLastExchange && !isPromptBlockingError && /* @__PURE__ */ React.createElement("div", {
		className: "DocSearch-AskAiScreen-Error",
		role: "alert"
	}, /* @__PURE__ */ React.createElement(AlertIcon, { "aria-hidden": "true" }), /* @__PURE__ */ React.createElement("div", { className: "DocSearch-AskAiScreen-Error-Content" }, /* @__PURE__ */ React.createElement("h4", { className: "DocSearch-AskAiScreen-Error-Title" }, errorTitleText), /* @__PURE__ */ React.createElement(MemoizedMarkdown, {
		content: askAiError.message,
		copyButtonText: "",
		copyButtonCopiedText: "",
		isStreaming: false
	}))), isThinking && /* @__PURE__ */ React.createElement("div", {
		className: "DocSearch-AskAiScreen-MessageContent-Thinking",
		role: "status"
	}, /* @__PURE__ */ React.createElement("span", { className: "DocSearch-shimmer" }, translations.thinkingText || "Thinking..."), /* @__PURE__ */ React.createElement("span", { className: "DocSearch-AskAi-Thinking-Skeleton DocSearch-shimmer" }), /* @__PURE__ */ React.createElement("span", { className: "DocSearch-AskAi-Thinking-Skeleton DocSearch-AskAi-Thinking-Skeleton--short DocSearch-shimmer" })), displayParts.map((part, idx) => {
		const index = idx;
		if (typeof part === "string") return /* @__PURE__ */ React.createElement(MemoizedMarkdown, {
			key: index,
			content: part,
			copyButtonText: translations.copyButtonText || "Copy",
			copyButtonCopiedText: translations.copyButtonCopiedText || "Copied!",
			isStreaming: loadingStatus === "streaming"
		});
		if (isAIToolPart(part)) return /* @__PURE__ */ React.createElement(ToolCall, {
			key: index,
			translations: toolCallTranslations,
			part,
			tools,
			memoryEnabled,
			onSearchQueryClick
		});
		if (part.type === "aggregated-tool-call") return /* @__PURE__ */ React.createElement(AggregatedSearchBlock, {
			key: index,
			queries: part.queries,
			translations,
			onSearchQueryClick
		});
		if (part.type === "reasoning" && part.state === "streaming") return /* @__PURE__ */ React.createElement("div", {
			key: index,
			className: "DocSearch-AskAiScreen-MessageContent-Reasoning DocSearch-shimmer"
		}, /* @__PURE__ */ React.createElement(LoadingIcon, { className: "DocSearch-AskAiScreen-SmallerLoadingIcon" }), /* @__PURE__ */ React.createElement("span", { className: "DocSearch-shimmer" }, "Reasoning..."));
		if (part.type === "text") return /* @__PURE__ */ React.createElement(MemoizedMarkdown, {
			key: index,
			content: part.text,
			copyButtonText: translations.copyButtonText || "Copy",
			copyButtonCopiedText: translations.copyButtonCopiedText || "Copied!",
			isStreaming: part.state === "streaming"
		});
		return null;
	})), wasStopped && /* @__PURE__ */ React.createElement("p", { className: "DocSearch-AskAiScreen-MessageContent-Stopped" }, stoppedStreamingText)), /* @__PURE__ */ React.createElement("div", { className: "DocSearch-AskAiScreen-Answer-Footer" }, /* @__PURE__ */ React.createElement(SourcesPanel, {
		links: urlsToDisplay,
		titleText: relatedSourcesText,
		pluralTitleText: relatedSourcesTextPlural
	}), /* @__PURE__ */ React.createElement(FeedbackActions, {
		id: messageId,
		showActions,
		latestAssistantMessageContent: assistantContent || null,
		translations,
		conversations,
		onFeedback
	})), promptSuggestions.length > 0 && /* @__PURE__ */ React.createElement(ConversationPromptSuggestions, {
		title: suggestedPromptsTitleText,
		suggestions: promptSuggestions,
		onSelectPromptSuggestion
	})));
}
function AskAiScreen(_ref) {
	let { translations = {} } = _ref, props = _objectWithoutProperties(_ref, _excluded$6);
	const { disclaimerText = "Answers are generated with AI which can make mistakes.", threadDepthExceededMessage = "This conversation is now closed to keep responses accurate.", startNewConversationButtonText = "Start a new conversation" } = translations;
	const { messages, tools, askAiError, status, memoryEnabled } = props;
	const hasPromptBlockingError = useMemo(() => {
		return status === "error" && isAskAiPromptBlockingError(askAiError);
	}, [status, askAiError]);
	const blockingMessage = useMemo(() => getAskAiBlockingBannerMessage(askAiError), [askAiError]);
	const showNewConversationLink = showAskAiBlockingBannerNewConversationLink(askAiError);
	const exchanges = useMemo(() => {
		const grouped = [];
		for (let i = 0; i < messages.length; i++) if (messages[i].role === "user") {
			var _messages;
			const userMessage = messages[i];
			const assistantMessage = ((_messages = messages[i + 1]) === null || _messages === void 0 ? void 0 : _messages.role) === "assistant" ? messages[i + 1] : null;
			grouped.push({
				id: userMessage.id,
				userMessage,
				assistantMessage
			});
			if (assistantMessage) i++;
		}
		return grouped;
	}, [messages]);
	const handleSearchQueryClick = (query) => {
		props.onAskAiToggle(false);
		props.setQuery(query);
	};
	const showBlockingBanner = hasPromptBlockingError && (!isThreadDepthError(askAiError) || messages.some((message) => message.role === "assistant"));
	return /* @__PURE__ */ React.createElement("div", { className: "DocSearch-AskAiScreen DocSearch-AskAiScreen-Container" }, /* @__PURE__ */ React.createElement("div", {
		id: props.promptBlockingErrorId,
		role: "alert"
	}, showBlockingBanner && /* @__PURE__ */ React.createElement("div", { className: "DocSearch-AskAiScreen-MessageContent DocSearch-AskAiScreen-Error DocSearch-AskAiScreen-Error--ThreadDepth" }, /* @__PURE__ */ React.createElement("div", { className: "DocSearch-AskAiScreen-Error-Content" }, /* @__PURE__ */ React.createElement("p", { className: "DocSearch-AskAiScreen-Error-Title" }, blockingMessage !== null && blockingMessage !== void 0 ? blockingMessage : isThreadDepthError(askAiError) ? threadDepthExceededMessage : "This conversation cannot continue."), showNewConversationLink && /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement("button", {
		type: "button",
		className: "DocSearch-ThreadDepthError-Link",
		onClick: props.onNewConversation
	}, startNewConversationButtonText), " ", "to continue.")))), /* @__PURE__ */ React.createElement(AskAiScreenDisclaimer, { disclaimerText }), /* @__PURE__ */ React.createElement("div", { className: "DocSearch-AskAiScreen-Body" }, /* @__PURE__ */ React.createElement("div", { className: "DocSearch-AskAiScreen-ExchangesList" }, exchanges.slice().reverse().map((exchange, index) => /* @__PURE__ */ React.createElement(AskAiExchangeCard, {
		key: exchange.id,
		exchange,
		askAiError: props.askAiError,
		isLastExchange: index === 0,
		loadingStatus: props.status,
		translations,
		tools,
		conversations: props.conversations,
		memoryEnabled,
		onSearchQueryClick: handleSearchQueryClick,
		onFeedback: props.onFeedback,
		onSelectPromptSuggestion: props.onSelectPromptSuggestion
	})))));
}

//#endregion
//#region src/components/ui/RecentConversationsResults.tsx
const _excluded$5 = ["translations"];
function RecentConversationsResults(_ref) {
	let { translations = {} } = _ref, props = _objectWithoutProperties(_ref, _excluded$5);
	const { recentConversationsTitle = "Resume AI conversations", removeRecentConversationButtonTitle = "Remove this conversation from history" } = translations;
	const recentConversations = getCollection(props.state, "recentConversations");
	if (!recentConversations) return null;
	return /* @__PURE__ */ React.createElement(Results, _objectSpread2(_objectSpread2({}, props), {}, {
		title: recentConversationsTitle,
		collection: recentConversations,
		renderIcon: () => /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Hit-icon DocSearch-Hit-icon--start" }, /* @__PURE__ */ React.createElement(MessageIcon, null)),
		renderAction: ({ item }) => /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Hit-action" }, /* @__PURE__ */ React.createElement("button", {
			className: "DocSearch-Hit-action-button",
			title: removeRecentConversationButtonTitle,
			type: "submit",
			onClick: (event) => {
				event.preventDefault();
				event.stopPropagation();
				props.conversations.remove(item);
				props.refresh();
			}
		}, /* @__PURE__ */ React.createElement(CloseIcon, null)))
	}));
}

//#endregion
//#region src/components/AskAiStartScreen.tsx
const _excluded$4 = ["translations"];
function AskAiStartScreen(_ref) {
	let { translations = {} } = _ref, props = _objectWithoutProperties(_ref, _excluded$4);
	return /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Dropdown-Container" }, /* @__PURE__ */ React.createElement(RecentConversationsResults, _objectSpread2(_objectSpread2({}, props), {}, { translations })), /* @__PURE__ */ React.createElement(StoredSearchesSections, _objectSpread2(_objectSpread2({}, props), {}, { translations })));
}

//#endregion
//#region src/ConversationHistoryScreen.tsx
const _excluded$3 = ["onAskAiToggle"];
function ConversationHistoryScreen(_ref) {
	let { onAskAiToggle } = _ref, props = _objectWithoutProperties(_ref, _excluded$3);
	const collection = React.useMemo(() => getCollection(props.state, "recentConversations"), [props.state]);
	React.useEffect(() => {
		if (!collection || collection.items.length === 0) onAskAiToggle(true);
	}, [collection, onAskAiToggle]);
	if (!collection) return null;
	return /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Dropdown-Container DocSearch-Conversation-History" }, /* @__PURE__ */ React.createElement(Results, _objectSpread2(_objectSpread2({}, props), {}, {
		key: collection.source.sourceId,
		title: "",
		translations: props.translations,
		collection,
		renderIcon: () => /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Hit-icon" }, /* @__PURE__ */ React.createElement(SparklesIcon, null)),
		renderAction: ({ item }) => /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Hit-action" }, /* @__PURE__ */ React.createElement("button", {
			type: "button",
			className: "DocSearch-Hit-action-button",
			onClick: (e) => {
				e.preventDefault();
				e.stopPropagation();
				props.conversations.remove(item);
				props.refresh();
			}
		}, /* @__PURE__ */ React.createElement(CloseIcon, null)))
	})));
}

//#endregion
//#region src/NewConversationScreen.tsx
function NewConversationScreen({ translations = {}, suggestedQuestions = [], selectSuggestedQuestion }) {
	const { newConversationTitle = "How can I help you today?", newConversationDescription = "I search through your documentation to help you find setup guides, feature details and troubleshooting tips, fast." } = translations;
	return /* @__PURE__ */ React.createElement("div", { className: "DocSearch-NewConversationScreen" }, /* @__PURE__ */ React.createElement("h3", { className: "DocSearch-NewConversationScreen-Title" }, newConversationTitle), /* @__PURE__ */ React.createElement("p", { className: "DocSearch-NewConversationScreen-Description" }, newConversationDescription), /* @__PURE__ */ React.createElement("div", { className: "DocSearch-NewConversationScreen-SuggestedQuestions" }, suggestedQuestions.map((question) => /* @__PURE__ */ React.createElement("button", {
		key: question.objectID,
		type: "button",
		className: "DocSearch-NewConversationScreen-SuggestedQuestion",
		onClick: () => selectSuggestedQuestion(question)
	}, question.question))));
}

//#endregion
//#region src/AskAiScreenState.tsx
const _excluded$2 = ["translations", "selectAskAiQuestion"];
const AskAiScreenState = React.memo((_ref) => {
	var _props$state;
	let { translations = {}, selectAskAiQuestion } = _ref, props = _objectWithoutProperties(_ref, _excluded$2);
	const handleSelectPromptSuggestion = React.useCallback((prompt) => {
		selectAskAiQuestion(true, prompt);
	}, [selectAskAiQuestion]);
	if (props.canHandleAskAi && props.isAskAiActive && props.askAiState === "conversation-history") return /* @__PURE__ */ React.createElement(ConversationHistoryScreen, _objectSpread2({ selectAskAiQuestion }, props));
	if (props.canHandleAskAi && props.isAskAiActive && props.askAiState === "new-conversation") return /* @__PURE__ */ React.createElement(NewConversationScreen, {
		translations: translations === null || translations === void 0 ? void 0 : translations.newConversation,
		selectSuggestedQuestion: props.selectSuggestedQuestion,
		suggestedQuestions: props.suggestedQuestions
	});
	if (props.isAskAiActive && props.canHandleAskAi) return /* @__PURE__ */ React.createElement(AskAiScreen, _objectSpread2(_objectSpread2({}, props), {}, {
		messages: props.messages,
		tools: props.tools,
		status: props.status,
		askAiError: props.askAiError,
		translations: translations === null || translations === void 0 ? void 0 : translations.askAiScreen,
		memoryEnabled: props.memoryEnabled,
		selectAskAiQuestion,
		onSelectPromptSuggestion: handleSelectPromptSuggestion
	}));
	if (((_props$state = props.state) === null || _props$state === void 0 ? void 0 : _props$state.status) === "error") return /* @__PURE__ */ React.createElement(ErrorScreen, { translations: translations === null || translations === void 0 ? void 0 : translations.errorScreen });
	if (isQueryEmpty(props.state.query)) return /* @__PURE__ */ React.createElement(AskAiStartScreen, _objectSpread2(_objectSpread2({}, props), {}, {
		hasCollections: props.hasCollections,
		translations: translations === null || translations === void 0 ? void 0 : translations.startScreen,
		selectAskAiQuestion
	}));
	if (!props.hasCollections && !props.canHandleAskAi) return /* @__PURE__ */ React.createElement(NoResultsScreen, _objectSpread2(_objectSpread2({}, props), {}, { translations: translations === null || translations === void 0 ? void 0 : translations.noResultsScreen }));
	return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ResultsScreen, _objectSpread2(_objectSpread2({}, props), {}, { translations: translations === null || translations === void 0 ? void 0 : translations.resultsScreen })), props.canHandleAskAi && !props.hasCollections && /* @__PURE__ */ React.createElement(NoResultsScreen, _objectSpread2(_objectSpread2({}, props), {}, { translations: translations === null || translations === void 0 ? void 0 : translations.noResultsScreen })));
}, function areEqual(prevProps, nextProps) {
	if (prevProps.isAskAiActive !== nextProps.isAskAiActive || prevProps.askAiState !== nextProps.askAiState || prevProps.canHandleAskAi !== nextProps.canHandleAskAi) return false;
	const isLoading = nextProps.state.status === "loading" || nextProps.state.status === "stalled";
	const isWaitingForCollections = nextProps.state.status === "idle" && !isQueryEmpty(nextProps.state.query) && prevProps.state.query !== nextProps.state.query && prevProps.state.collections === nextProps.state.collections;
	return isLoading || isWaitingForCollections;
});

//#endregion
//#region src/ModalHeading.tsx
function ModalHeading({ heading, shimmer = false }) {
	return /* @__PURE__ */ React.createElement("span", { className: `DocSearch-Modal-heading${shimmer ? " DocSearch-shimmer" : ""}` }, heading);
}

//#endregion
//#region src/components/AskAiSearchBox.tsx
const _excluded$1 = [
	"translations",
	"askAiState",
	"onAskAiToggle",
	"setAskAiState"
];
function AskAiSearchBox(_ref) {
	let { translations = {}, askAiState, onAskAiToggle, setAskAiState } = _ref, props = _objectWithoutProperties(_ref, _excluded$1);
	const { clearButtonTitle = "Clear", clearButtonAriaLabel = "Clear the query", closeButtonText = "Close", closeButtonAriaLabel = "Close", searchInputLabel = "Search", backToKeywordSearchButtonText = "Back to keyword search", backToKeywordSearchButtonAriaLabel = "Back to keyword search", placeholderTextAskAiStreaming = "Answering...", newConversationPlaceholder = "Ask a question", conversationHistoryTitle = "My conversation history", startNewConversationText = "Start a new conversation", viewConversationHistoryText = "Conversation history", threadDepthErrorPlaceholder = "Conversation limit reached" } = translations;
	const hasRecentConversations = React.useMemo(() => {
		const askAiSource = getCollection(props.state, "recentConversations");
		if (!askAiSource) return false;
		return askAiSource.items.length > 0;
	}, [props.state]);
	const baseInputProps = props.getInputProps({
		inputElement: props.inputRef.current,
		autoFocus: props.autoFocus
	});
	const blockedKeys = /* @__PURE__ */ new Set([
		"ArrowUp",
		"ArrowDown",
		"Enter"
	]);
	const origOnKeyDown = baseInputProps.onKeyDown;
	const origOnChange = baseInputProps.onChange;
	const isAskAiStreaming = props.askAiStatus === "streaming" || props.askAiStatus === "submitted";
	const renderMoreOptions = props.isAskAiActive && askAiState !== "conversation-history";
	const isPromptBlocked = Boolean(props.promptBlockingChrome);
	let searchPlaceholder = props.placeholder;
	if (askAiState === "new-conversation") searchPlaceholder = newConversationPlaceholder;
	if (props.promptBlockingChrome === "full" && props.isAskAiActive) searchPlaceholder = threadDepthErrorPlaceholder;
	let heading = null;
	if (isAskAiStreaming) heading = placeholderTextAskAiStreaming;
	if (askAiState === "conversation-history") heading = conversationHistoryTitle;
	React.useEffect(() => {
		if (props.askAiStatus !== "streaming" && props.askAiStatus !== "submitted" && props.inputRef.current) props.inputRef.current.focus();
	}, [props.askAiStatus, props.inputRef]);
	/**
	* We need to block the default behavior of the input when Ask AI is active.
	* This is because the input is used to ask another question when the user
	* presses enter.
	*
	* Learn more on default autocomplete behavior:
	* https://github.com/algolia/autocomplete/blob/next/packages/autocomplete-core/src/getDefaultProps.ts.
	*/
	const inputProps = {
		enterKeyHint: props.isAskAiActive ? "enter" : "search",
		onKeyDown: (e) => {
			if (isPromptBlocked && props.isAskAiActive && blockedKeys.has(e.key)) {
				e.preventDefault();
				e.stopPropagation();
				return;
			}
			let askAiHandled = false;
			if (props.isAskAiActive && blockedKeys.has(e.key)) {
				if (e.key === "Enter" && !isAskAiStreaming && !isQueryEmpty(props.state.query)) props.onAskAgain(props.state.query);
				askAiHandled = true;
			}
			if (askAiHandled) {
				e.preventDefault();
				e.stopPropagation();
				return;
			}
			origOnKeyDown === null || origOnKeyDown === void 0 || origOnKeyDown(e);
		},
		onChange: (e) => {
			if (isPromptBlocked && props.isAskAiActive) {
				e.preventDefault();
				return;
			}
			if (props.isAskAiActive) {
				props.setQuery(e.currentTarget.value);
				e.preventDefault();
				e.stopPropagation();
				return;
			}
			origOnChange === null || origOnChange === void 0 || origOnChange(e);
		},
		disabled: isAskAiStreaming,
		readOnly: isPromptBlocked && props.isAskAiActive,
		"aria-disabled": isPromptBlocked && props.isAskAiActive,
		"aria-describedby": isPromptBlocked && props.isAskAiActive ? props.promptBlockingErrorId : void 0
	};
	const handleAskAiBackClick = React.useCallback(() => {
		if (isPromptBlocked) {
			props.onNewConversation();
			return;
		}
		if (askAiState === "conversation-history") {
			onAskAiToggle(true);
			setAskAiState("initial");
			return;
		}
		onAskAiToggle(false);
	}, [
		askAiState,
		isPromptBlocked,
		onAskAiToggle,
		setAskAiState,
		props
	]);
	const leadingElement = props.isAskAiActive ? /* @__PURE__ */ React.createElement("button", {
		type: "button",
		tabIndex: 0,
		className: "DocSearch-Action DocSearch-AskAi-Return",
		title: isPromptBlocked ? startNewConversationText : backToKeywordSearchButtonText,
		"aria-label": isPromptBlocked ? startNewConversationText : backToKeywordSearchButtonAriaLabel,
		onClick: handleAskAiBackClick
	}, /* @__PURE__ */ React.createElement(BackIcon, null)) : void 0;
	const inputOverlay = heading ? /* @__PURE__ */ React.createElement(ModalHeading, {
		heading,
		shimmer: isAskAiStreaming
	}) : null;
	const actionsBeforeClose = /* @__PURE__ */ React.createElement(React.Fragment, null, !props.isAskAiActive && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("button", {
		className: "DocSearch-Clear",
		type: "reset",
		"aria-label": clearButtonAriaLabel,
		hidden: !props.state.query,
		tabIndex: props.state.query ? 0 : -1,
		"aria-hidden": !props.state.query ? "true" : "false"
	}, clearButtonTitle), props.state.query && /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Divider" })), isAskAiStreaming && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("button", {
		type: "button",
		className: "DocSearch-Action DocSearch-StopStreaming",
		onClick: props.onStopAskAiStreaming
	}, /* @__PURE__ */ React.createElement(StopIcon, null)), /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Divider" })), renderMoreOptions && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Menu, null, /* @__PURE__ */ React.createElement(Menu.Trigger, { className: "DocSearch-Action" }, /* @__PURE__ */ React.createElement(MoreVerticalIcon, null)), /* @__PURE__ */ React.createElement(Menu.Content, null, /* @__PURE__ */ React.createElement(Menu.Item, { onClick: props.onNewConversation }, /* @__PURE__ */ React.createElement(NewConversationIcon, null), startNewConversationText), hasRecentConversations && /* @__PURE__ */ React.createElement(Menu.Item, { onClick: props.onViewConversationHistory }, /* @__PURE__ */ React.createElement(ConversationHistoryIcon, null), viewConversationHistoryText))), /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Divider" })));
	return /* @__PURE__ */ React.createElement(SearchBoxForm, _objectSpread2(_objectSpread2({}, props), {}, {
		placeholder: searchPlaceholder,
		clearButtonTitle,
		clearButtonAriaLabel,
		closeButtonText,
		closeButtonAriaLabel,
		searchInputLabel,
		leadingElement,
		inputOverlay,
		hideInput: Boolean(heading),
		actionsBeforeClose,
		inputProps
	}));
}

//#endregion
//#region src/utils/createAskAiSources.ts
const MAX_RECENT_CONVERSATIONS_DISPLAYED = 3;
const EMPTY_RESULT_CONTENT = {
	value: "",
	matchLevel: "none",
	matchedWords: []
};
const EMPTY_HIERARCHY_HIGHLIGHT_RESULT = {
	lvl0: EMPTY_RESULT_CONTENT,
	lvl1: EMPTY_RESULT_CONTENT,
	lvl2: EMPTY_RESULT_CONTENT,
	lvl3: EMPTY_RESULT_CONTENT,
	lvl4: EMPTY_RESULT_CONTENT,
	lvl5: EMPTY_RESULT_CONTENT,
	lvl6: EMPTY_RESULT_CONTENT
};
const EMPTY_HIGHLIGHT_RESULT = {
	content: EMPTY_RESULT_CONTENT,
	hierarchy: EMPTY_HIERARCHY_HIGHLIGHT_RESULT,
	hierarchy_camel: []
};
const EMPTY_SNIPPET_RESULT = {
	content: EMPTY_RESULT_CONTENT,
	hierarchy: EMPTY_HIERARCHY_HIGHLIGHT_RESULT,
	hierarchy_camel: []
};
function buildRecentConversationSources({ conversations, disableUserPersonalization, handleSelectStoredConversation }) {
	return [{
		sourceId: SOURCE_IDS.recentConversations,
		getItems() {
			if (disableUserPersonalization) return [];
			return conversations.getAll().slice(0, MAX_RECENT_CONVERSATIONS_DISPLAYED);
		},
		onSelect({ item, event }) {
			if (item.type !== "askAI") return;
			handleSelectStoredConversation(item, event);
		}
	}];
}
function createAskAiHit({ query, objectID, lvl0 }) {
	return {
		type: "askAI",
		query,
		url_without_anchor: "",
		objectID,
		content: null,
		url: "",
		anchor: null,
		hierarchy: {
			lvl0,
			lvl1: query,
			lvl2: null,
			lvl3: null,
			lvl4: null,
			lvl5: null,
			lvl6: null
		},
		_highlightResult: EMPTY_HIGHLIGHT_RESULT,
		_snippetResult: EMPTY_SNIPPET_RESULT,
		__docsearch_parent: null
	};
}
async function getPromptSuggestions({ query, indexName, searchClient, hitsPerPage = 3 }) {
	try {
		const { results } = await searchClient.search({ requests: [{
			query,
			indexName,
			hitsPerPage,
			attributesToRetrieve: ["prompt"]
		}] });
		return results[0].hits.map((hit) => createAskAiHit({
			query: hit.prompt,
			objectID: hit.objectID,
			lvl0: "Prompt Suggestions"
		}));
	} catch (_unused) {
		return [];
	}
}
async function buildAskAiActionSources({ query, handleSelectAskAiQuestion, promptSuggestionsOptions, searchClient }) {
	const promptSuggestions = promptSuggestionsOptions ? await getPromptSuggestions({
		query,
		indexName: promptSuggestionsOptions.indexName,
		hitsPerPage: promptSuggestionsOptions.hitsPerPage,
		searchClient
	}) : [];
	return [{
		sourceId: SOURCE_IDS.askAI,
		getItems() {
			return [createAskAiHit({
				query,
				objectID: "ask-ai-button",
				lvl0: "Ask AI"
			}), ...promptSuggestions];
		},
		onSelect({ item }) {
			if (item.type === "askAI" && item.query) handleSelectAskAiQuestion(true, item.query);
		}
	}];
}

//#endregion
//#region src/DocSearchAskAiModal.tsx
const _excluded = [
	"appId",
	"apiKey",
	"askAi",
	"maxResultsPerGroup",
	"theme",
	"onClose",
	"transformItems",
	"hitComponent",
	"resultsFooterComponent",
	"navigator",
	"initialScrollY",
	"transformSearchClient",
	"disableUserPersonalization",
	"initialQuery",
	"translations",
	"getMissingResultsUrl",
	"insights",
	"onAskAiToggle",
	"interceptAskAiEvent",
	"isAskAiActive",
	"recentSearchesLimit",
	"recentSearchesWithFavoritesLimit",
	"indices",
	"facets",
	"isHybridModeSupported",
	"footerAction"
];
const _excluded2 = [
	"footer",
	"searchBox",
	"facets"
];
function DocSearchAskAiModal(_ref) {
	var _translations$searchB, _askAiConfig$memory$e, _askAiConfig$memory, _askAiConfig$tools;
	let { appId, apiKey, askAi, maxResultsPerGroup, theme, onClose = noop, transformItems = identity, hitComponent = Hit, resultsFooterComponent = () => null, navigator, initialScrollY = 0, transformSearchClient = identity, disableUserPersonalization = false, initialQuery: initialQueryFromProp = "", translations = {}, getMissingResultsUrl, insights = false, onAskAiToggle, interceptAskAiEvent, isAskAiActive = false, recentSearchesLimit = 7, recentSearchesWithFavoritesLimit = 4, indices, facets, isHybridModeSupported = false, footerAction } = _ref, props = _objectWithoutProperties(_ref, _excluded);
	const { footer: footerTranslations, searchBox: searchBoxTranslations, facets: facetBarTranslations } = translations, screenStateTranslations = _objectWithoutProperties(translations, _excluded2);
	const [state, setState] = React.useState({
		query: "",
		collections: [],
		completion: null,
		context: {},
		isOpen: false,
		activeItemId: null,
		status: "idle"
	});
	const canHandleAskAi = Boolean(askAi);
	let placeholder = (translations === null || translations === void 0 || (_translations$searchB = translations.searchBox) === null || _translations$searchB === void 0 ? void 0 : _translations$searchB.placeholderText) || props.placeholder || "Search docs";
	if (canHandleAskAi) {
		var _translations$searchB2;
		placeholder = (translations === null || translations === void 0 || (_translations$searchB2 = translations.searchBox) === null || _translations$searchB2 === void 0 ? void 0 : _translations$searchB2.placeholderText) || "Search docs or ask AI a question";
	}
	if (isAskAiActive) {
		var _translations$searchB3;
		placeholder = (translations === null || translations === void 0 || (_translations$searchB3 = translations.searchBox) === null || _translations$searchB3 === void 0 ? void 0 : _translations$searchB3.placeholderTextAskAi) || "Ask another question...";
	}
	const { containerRef, modalRef, formElementRef, dropdownRef, inputRef } = useModalRefs();
	const promptBlockingErrorId = React.useId();
	const { initialQuery, initialQueryFromSelection } = useInitialModalQuery(initialQueryFromProp);
	const searchClient = useSearchClient(appId, apiKey, transformSearchClient);
	const askAiConfig = typeof askAi === "object" ? askAi : null;
	const askAiConfigurationId = askAiConfig ? askAiConfig.agentId : askAi;
	const askAiSearchParameters = askAiConfig === null || askAiConfig === void 0 ? void 0 : askAiConfig.searchParameters;
	const [askAiState, setAskAiState] = React.useState("initial");
	const suggestedQuestions = useSuggestedQuestions({
		agentId: askAiConfigurationId,
		searchClient,
		suggestedQuestionsEnabled: askAiConfig === null || askAiConfig === void 0 ? void 0 : askAiConfig.suggestedQuestions
	});
	const memoryEnabled = (_askAiConfig$memory$e = askAiConfig === null || askAiConfig === void 0 || (_askAiConfig$memory = askAiConfig.memory) === null || _askAiConfig$memory === void 0 ? void 0 : _askAiConfig$memory.enabled) !== null && _askAiConfig$memory$e !== void 0 ? _askAiConfig$memory$e : false;
	const tools = (_askAiConfig$tools = askAiConfig === null || askAiConfig === void 0 ? void 0 : askAiConfig.tools) !== null && _askAiConfig$tools !== void 0 ? _askAiConfig$tools : EMPTY_TOOLS;
	const indexes = React.useMemo(() => normalizeDocSearchIndexes({ indices }), [indices]);
	const defaultIndexName = indexes[0].name;
	const autocompleteRef = React.useRef(void 0);
	const { visibleFacets, facetSelections, facetSelectionsRef, handleFacetSelectionChange, clearFacetSelections } = useDocSearchFacets({
		facets,
		indexes,
		searchClient,
		onSelectionsChange: () => {
			var _autocompleteRef$curr;
			return (_autocompleteRef$curr = autocompleteRef.current) === null || _autocompleteRef$curr === void 0 ? void 0 : _autocompleteRef$curr.refresh();
		}
	});
	const { favoriteSearches, recentSearches } = useStoredDocSearches({
		defaultIndexName,
		recentSearchesLimit,
		recentSearchesWithFavoritesLimit
	});
	const [stoppedStream, setStoppedStream] = React.useState(false);
	const { chatId, messages, status, sendMessage, stopAskAiStreaming, askAiError, sendFeedback, conversations, startNewConversation, restoreConversation } = useAskAi({
		agentId: askAiConfigurationId,
		apiKey: (askAiConfig === null || askAiConfig === void 0 ? void 0 : askAiConfig.apiKey) || apiKey,
		appId: (askAiConfig === null || askAiConfig === void 0 ? void 0 : askAiConfig.appId) || appId,
		searchParameters: askAiSearchParameters,
		tools,
		memory: askAiConfig === null || askAiConfig === void 0 ? void 0 : askAiConfig.memory,
		indices: askAiConfig === null || askAiConfig === void 0 ? void 0 : askAiConfig.indices
	});
	const prevStatus = React.useRef(status);
	React.useEffect(() => {
		if (disableUserPersonalization) return;
		if (prevStatus.current === "streaming" && status === "ready") {
			if (stoppedStream && messages.at(-1)) messages.at(-1).metadata = { stopped: true };
			for (const part of messages[0].parts) if (part.type === "text") conversations.add(buildDummyAskAiHit(part.text, messages, chatId));
		}
		prevStatus.current = status;
	}, [
		status,
		messages,
		conversations,
		disableUserPersonalization,
		stoppedStream,
		chatId
	]);
	const shouldBlockPrompt = React.useMemo(() => {
		return status === "error" && isAskAiPromptBlockingError(askAiError);
	}, [status, askAiError]) && (!isThreadDepthError(askAiError) || messages.some((message) => message.role === "assistant"));
	const promptBlockingChrome = React.useMemo(() => {
		if (!shouldBlockPrompt || askAiState === "new-conversation" || askAiState === "conversation-history") return;
		return isAgentStudioTokenOutputLimitError(askAiError) ? "minimal" : "full";
	}, [
		askAiError,
		askAiState,
		shouldBlockPrompt
	]);
	const saveRecentSearch = useSaveRecentSearch({
		favoriteSearches,
		recentSearches,
		disableUserPersonalization
	});
	const sendItemClickEvent = useSendItemClickEvent(state);
	const handleSelectAskAiQuestion = React.useCallback((toggle, query, suggestedQuestion = void 0) => {
		if (toggle) {
			const initialMessage = {
				query,
				suggestedQuestionId: suggestedQuestion === null || suggestedQuestion === void 0 ? void 0 : suggestedQuestion.objectID
			};
			if (interceptAskAiEvent === null || interceptAskAiEvent === void 0 ? void 0 : interceptAskAiEvent(initialMessage)) {
				if (autocompleteRef.current) autocompleteRef.current.setQuery("");
				return;
			}
		}
		if (toggle && askAiState === "new-conversation") setAskAiState("initial");
		onAskAiToggle(toggle, {
			query,
			suggestedQuestionId: suggestedQuestion === null || suggestedQuestion === void 0 ? void 0 : suggestedQuestion.objectID
		});
		if (isHybridModeSupported) return;
		setStoppedStream(false);
		const messageOptions = {};
		if (suggestedQuestion) messageOptions.body = { suggestedQuestionId: suggestedQuestion.objectID };
		sendMessage({
			role: "user",
			parts: [{
				type: "text",
				text: query
			}]
		}, messageOptions);
		if (dropdownRef.current) {
			const el = dropdownRef.current;
			if (typeof el.scrollTo === "function") el.scrollTo({
				top: 0,
				behavior: "smooth"
			});
			else el.scrollTop = 0;
		}
		if (autocompleteRef.current) autocompleteRef.current.setQuery("");
	}, [
		askAiState,
		onAskAiToggle,
		isHybridModeSupported,
		sendMessage,
		dropdownRef,
		interceptAskAiEvent
	]);
	const handleFeedbackSubmit = React.useCallback(async (messageId, feedback) => {
		if (!askAiConfigurationId || !appId) return;
		await sendFeedback(messageId, feedback);
	}, [
		askAiConfigurationId,
		appId,
		sendFeedback
	]);
	const handleSelectStoredConversation = React.useCallback((item, event) => {
		if (isQueryEmpty(item.query) || !item.messages || item.messages.length < 1) return;
		const hitMessages = item.messages;
		const initialMessage = {
			query: item.query,
			messageId: hitMessages[0].id
		};
		restoreConversation(hitMessages, item.chatId);
		if (interceptAskAiEvent === null || interceptAskAiEvent === void 0 ? void 0 : interceptAskAiEvent(initialMessage)) {
			if (autocompleteRef.current) autocompleteRef.current.setQuery("");
			event.preventDefault();
			return;
		}
		onAskAiToggle(true, initialMessage);
	}, [
		interceptAskAiEvent,
		restoreConversation,
		onAskAiToggle
	]);
	if (!autocompleteRef.current) autocompleteRef.current = createAutocomplete({
		id: "docsearch",
		defaultActiveItemId: 0,
		openOnFocus: true,
		initialState: {
			query: initialQuery,
			context: { searchSuggestions: [] }
		},
		insights: Boolean(insights),
		navigator,
		onStateChange(changes) {
			setState(changes.state);
		},
		async getSources({ query, state: sourcesState, setContext, setStatus }) {
			if (isQueryEmpty(query)) {
				const noQuerySources = buildNoQuerySources({
					recentSearches,
					favoriteSearches,
					saveRecentSearch,
					onClose,
					disableUserPersonalization
				});
				return [...canHandleAskAi ? buildRecentConversationSources({
					conversations,
					disableUserPersonalization,
					handleSelectStoredConversation
				}) : [], ...noQuerySources];
			}
			const querySourcesState = { context: sourcesState.context };
			const algoliaSourcesPromise = buildQuerySources({
				query,
				state: querySourcesState,
				setContext,
				setStatus,
				searchClient,
				indexes,
				insights: Boolean(insights),
				appId,
				apiKey,
				maxResultsPerGroup,
				transformItems,
				saveRecentSearch,
				onClose,
				facetSelections: facetSelectionsRef
			});
			const askAiSourcesPromise = canHandleAskAi ? buildAskAiActionSources({
				query,
				handleSelectAskAiQuestion,
				promptSuggestionsOptions: askAiConfig === null || askAiConfig === void 0 ? void 0 : askAiConfig.promptSuggestions,
				searchClient
			}) : Promise.resolve([]);
			const [askAiSources, algoliaSources] = await Promise.all([askAiSourcesPromise, algoliaSourcesPromise]);
			return [...askAiSources, ...algoliaSources];
		}
	});
	const autocomplete = autocompleteRef.current;
	const { getEnvironmentProps, getRootProps, refresh } = autocomplete;
	useModalEnvironment({
		getEnvironmentProps,
		containerRef,
		dropdownRef,
		formElementRef,
		inputRef,
		initialScrollY,
		modalRef,
		theme
	});
	React.useEffect(() => {
		if (dropdownRef.current && !isAskAiActive) scrollTo(dropdownRef.current);
	}, [
		state.query,
		isAskAiActive,
		dropdownRef
	]);
	useRefreshOnInitialQuery({
		initialQuery,
		inputRef,
		refresh
	});
	const hasCurrentMessages = messages.length > 0;
	const previousIsAskAiActive = React.useRef(isAskAiActive);
	React.useEffect(() => {
		const wasAskAiActive = previousIsAskAiActive.current;
		previousIsAskAiActive.current = isAskAiActive;
		if (!isAskAiActive) {
			autocomplete.refresh();
			if (wasAskAiActive && hasCurrentMessages) startNewConversation();
		}
	}, [
		isAskAiActive,
		autocomplete,
		startNewConversation,
		hasCurrentMessages
	]);
	React.useEffect(() => {
		setAskAiState("initial");
	}, [isAskAiActive, setAskAiState]);
	const onStopAskAiStreaming = async () => {
		setStoppedStream(true);
		await stopAskAiStreaming();
	};
	const handleNewConversation = () => {
		startNewConversation();
		setAskAiState("new-conversation");
	};
	const handleViewConversationHistory = () => {
		setAskAiState("conversation-history");
	};
	const selectSuggestedQuestion = (suggestedQuestion) => {
		handleSelectAskAiQuestion(true, suggestedQuestion.question, suggestedQuestion);
	};
	let showDocsearchDropdown = true;
	const hasCollections = state.collections.some((collection) => collection.source.sourceId !== SOURCE_IDS.askAI && collection.items.length > 0);
	if (state.status === "idle" && hasCollections === false && isQueryEmpty(state.query) && !isAskAiActive) showDocsearchDropdown = false;
	return /* @__PURE__ */ React.createElement(ModalShell, {
		state,
		containerRef,
		modalRef,
		formElementRef,
		dropdownRef,
		getRootProps,
		showDropdown: showDocsearchDropdown,
		searchBox: /* @__PURE__ */ React.createElement(AskAiSearchBox, _objectSpread2(_objectSpread2({}, autocomplete), {}, {
			state,
			placeholder: placeholder || "Search docs",
			autoFocus: isQueryEmpty(initialQuery),
			inputRef,
			isFromSelection: Boolean(initialQuery) && initialQuery === initialQueryFromSelection,
			translations: searchBoxTranslations,
			isAskAiActive,
			askAiStatus: status,
			askAiError,
			askAiState,
			setAskAiState,
			promptBlockingChrome,
			promptBlockingErrorId,
			onClose,
			onAskAiToggle,
			onAskAgain: (query) => {
				handleSelectAskAiQuestion(true, query);
			},
			onStopAskAiStreaming,
			onNewConversation: handleNewConversation,
			onViewConversationHistory: handleViewConversationHistory
		})),
		filterBar: !isAskAiActive && !isQueryEmpty(state.query) ? /* @__PURE__ */ React.createElement(FacetBar, {
			facets: visibleFacets,
			selections: facetSelections,
			translations: facetBarTranslations,
			clearSelections: clearFacetSelections,
			onSelectionChange: handleFacetSelectionChange
		}) : null,
		screenState: /* @__PURE__ */ React.createElement(AskAiScreenState, _objectSpread2(_objectSpread2({}, autocomplete), {}, {
			indexName: defaultIndexName,
			state,
			hitComponent,
			resultsFooterComponent,
			disableUserPersonalization,
			recentSearches,
			favoriteSearches,
			conversations,
			inputRef,
			translations: screenStateTranslations,
			getMissingResultsUrl,
			isAskAiActive,
			canHandleAskAi,
			messages,
			tools,
			askAiError,
			status,
			hasCollections,
			askAiState,
			selectAskAiQuestion: handleSelectAskAiQuestion,
			suggestedQuestions,
			selectSuggestedQuestion,
			memoryEnabled,
			resultBadgeKey: props.resultBadgeKey,
			promptBlockingErrorId,
			onAskAiToggle,
			onNewConversation: handleNewConversation,
			onItemClick: (item, event) => {
				if (item.type === "askAI" && item.query) {
					if (item.anchor === "stored") handleSelectStoredConversation(item, event);
					else handleSelectAskAiQuestion(true, item.query);
					setAskAiState("initial");
					event.preventDefault();
					return;
				}
				sendItemClickEvent(item);
				saveRecentSearch(item);
				if (!isModifierEvent(event)) onClose();
			},
			onFeedback: handleFeedbackSubmit
		})),
		footer: /* @__PURE__ */ React.createElement(Footer, {
			translations: footerTranslations,
			isAskAiActive,
			footerAction
		}),
		onClose
	});
}

//#endregion
export { DocSearchAskAiModal as t };
//# sourceMappingURL=DocSearchAskAiModal-DC2vpkpq.js.map